import { Injectable } from '@angular/core';
import { forkJoin, iif, Observable, of } from 'rxjs';
import { MapsAPILoader } from '@agm/core';
import { catchError, concatMap, delay, filter, map, mapTo, tap } from 'rxjs/operators';
import { TripLatng } from './trip-latng.model';
import { Trip } from './trip.model';
import { TripService } from './trip.service';
import { FIREBASE_CONFIG } from '../../core/firebase.config';
import { TripPingLight } from './trip-ping-light.model';
import { FirebaseTripPing } from './firebase-trip-ping.model';
import { AngularFireDatabase } from '@angular/fire/database';
import { TripDevicePath } from './trip-path.model';
import { LoadHandover } from '../../load-handover/shared/load-handover.model';
import { TripEvent } from './trip-event.model';
import { EtaReport } from './eta-report.model';
import * as moment from 'moment';
import { Moment } from 'moment';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { RefreshDataStatus, RefreshEtas } from './refresh-etas.model';
import { TripRefreshDistance } from './trip-refresh-distance.model';
import { DistanceUnit } from '../../shared/distance-unit';
import { PathSearchCriteria } from '../../shared/path-search-criteria.model';
import { RoutePathService } from '../../shared/services/route-path.service';
import { TranslateService } from '@ngx-translate/core';
import { Carrier } from '../../user-dashboard/shared/carrier.model';
import { LoadHandoverService } from '../../load-handover/shared/load-handover.service';
import { Latng } from '../../shared/latng';
import { GeocodingService } from '../../shared/services/geocoding.service';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { AnalyticsEvents, AnalyticsService } from '../../shared/analytics/analytics.service';
import { TripPathUtils } from './trip-path-utils';
import LatLngLiteral = google.maps.LatLngLiteral;

interface ForecastAndETA { forecastPath: LatLngLiteral[] }

@Injectable()
export class TripManagementService {
  metricsDistance = new Map<string, string>();
  imperialDistance = new Map<string, string>();

  constructor(
    private tripService: TripService,
    private loadHandoverService: LoadHandoverService,
    private authService: AuthenticationService,
    private af: AngularFireDatabase,
    private translate: TranslateService,
    private mapsAPILoader: MapsAPILoader,
    private routePathService: RoutePathService,
    private geocodingService: GeocodingService,
    private errorHandler: ErrorHandlerService,
    private analyticsService: AnalyticsService,
  ) {
  }

  initTrip(trip: Trip, singleTrip: boolean): Observable<LatLngLiteral[]> {
    let positionsObservable: Observable<LatLngLiteral[]>;

    // Send a google analytics event to see the ratio of clicks on the "more infos" button
    // (if we see it is clicked very often, we may consider enabling the detailed mode by default)
    const event = (singleTrip) ? 'Customer Trip shown' : 'Operator Trip shown';
    this.analyticsService.createEvent(AnalyticsEvents.LOAD_TRACKING_TRIP_CHANGED, {
      hitType: 'event',
      eventCategory: 'Metrics',
      eventAction: event,
      eventLabel: event
    });

    if (singleTrip) {
      positionsObservable = forkJoin([
        of(null),
        iif(
          () => !!trip.destPosition,
          of(trip.destPosition),
          this.tripService.resolveAndGetTripOriginAndDestinationLatng(trip, singleTrip).pipe(
            concatMap((tripLatng: TripLatng) => {
              return of([tripLatng.origin, tripLatng.destination])
            }))
        )
      ]);
    } else if (trip.originPosition && trip.destPosition) {
      positionsObservable = of([trip.originPosition, trip.destPosition]);
    } else {
      positionsObservable = this.tripService.resolveAndGetTripOriginAndDestinationLatng(trip, singleTrip).pipe(
        concatMap((tripLatng: TripLatng) => {
          return of([tripLatng.origin, tripLatng.destination])
        }))
    }
    return positionsObservable;
  }

  getTripPositionObservable(trip: Trip): Observable<TripPingLight> {
    return this.af.object(`/geotracking/${FIREBASE_CONFIG.env}/trips/live/${trip.carrier.id}/${trip.customerId}`)
      .valueChanges()
      .pipe(
        filter(p => !!p),
        map(p => TripPingLight.fromFirebase(FirebaseTripPing.fromDto(p))),
      )
  }

  refreshTripDetails(
    trip: Trip,
    singleTrip = true,
    detailedMode = false,
    forceRefresh = false,
    onTripChange = false,
    showAchievedPath: boolean,
    showForecastPath: boolean,
    latestTripDetailsRefreshedAt: Moment = null,
    latestTripRemainingDistanceRefreshedAt: Moment = null,
    distanceUnit = DistanceUnit.KILOMETERS,
    keepPingsWithHighAccuracy = false,
    excludeIsolatedPings = true,
    hidePingsAfterDestinationHit = false
  ) {
    if (trip == null) {
      return {
        observable: of(),
        latestTripDetailsRefreshedAt: null,
        latestTripDistanceRefreshedAt: null
      };
    }

    let refreshTripObservable                                           = of(trip);
    let refreshTripLatestPingObservable                                 = of(trip.latestPing);
    let refreshTripETAObservable                                        = of([]);
    let refreshTripRemainingDistanceObservable                          = of([]);
    let refreshTripAchievedPathObservable: Observable<TripDevicePath[]> = of([]);
    let refreshTripForecastPathObservable                               = of({ forecastPath: [] });
    let refreshTripHandoversObservable: Observable<LoadHandover[]>      = of([]);
    let refreshTripEventsObservable: Observable<TripEvent[]>            = of([]);
    let refreshTripEtasObservable: Observable<EtaReport[]>              = of([]);

    let tripDetailsRefreshedAt = null;
    if (forceRefresh || onTripChange) {
      refreshTripObservable = this.refreshTrip(trip, singleTrip);
      if (trip.isInProgress() && forceRefresh) {
        refreshTripETAObservable = this.refreshTripETA(trip, singleTrip, forceRefresh, latestTripDetailsRefreshedAt);
        tripDetailsRefreshedAt = moment();
      }
    } else if (!trip.latestPing && !singleTrip) {
      refreshTripLatestPingObservable = this.refreshTripLatestPing(trip);
    }

    let tripDistanceRefreshedAt = null;
    if (detailedMode) {
      refreshTripRemainingDistanceObservable = this.refreshTripRemainingDistance(trip, singleTrip, detailedMode, latestTripRemainingDistanceRefreshedAt);
      tripDistanceRefreshedAt = moment();
    }
    if (showAchievedPath) {
      refreshTripAchievedPathObservable = this.refreshTripAchievedPath(trip, forceRefresh, keepPingsWithHighAccuracy, excludeIsolatedPings, hidePingsAfterDestinationHit);
    }
    if (showForecastPath) {
      refreshTripForecastPathObservable = this.refreshTripForecastPath(trip, forceRefresh, distanceUnit, singleTrip);
    }

    if (!singleTrip) {
      refreshTripHandoversObservable = this.refreshTripHandovers(trip, trip.carrier);
      refreshTripEventsObservable = this.refreshTripEvents(trip);
      refreshTripEtasObservable = this.refreshTripEtas(trip);
    }

    return {
      observable: forkJoin([
        refreshTripObservable,
        refreshTripLatestPingObservable,
        refreshTripETAObservable,
        refreshTripRemainingDistanceObservable,
        refreshTripAchievedPathObservable,
        refreshTripForecastPathObservable,
        refreshTripHandoversObservable,
        refreshTripEventsObservable,
        refreshTripEtasObservable
      ]),
      latestTripDetailsRefreshedAt: tripDetailsRefreshedAt,
      latestTripDistanceRefreshedAt: tripDistanceRefreshedAt
    };
  }

  refreshTrip(trip: Trip, singleTrip: boolean): Observable<Trip> {
    return iif(
      () => singleTrip,
      this.tripService.getLightById(trip.customerId),
      this.tripService.getById(trip.tripId)
    ).pipe(
      tap(t => {
        trip.driverEta           = t.driverEta;
        trip.driverEtaUpdatedAt  = t.driverEtaUpdatedAt;
        trip.latestPing          = t.latestPing;
        trip.latestPingCreatedAt = t.latestPingCreatedAt;
        trip.status              = t.status;
        trip.completedAt         = t.completedAt;
      }),
      catchError(e => this.errorHandler.rethrowHttpError(e, 'realtime-tracking.load-tracking-map.trip-details.errors.refresh-details'))
    );
  }

  refreshTripLatestPing(trip: Trip): Observable<TripPingLight> {
    return this.tripService.getLatestPing(trip.tripId)
      .pipe(
        filter(p => !!p),
        tap(p => {
          trip.latestPing          = p;
          trip.latestPingCreatedAt = p.createdAt;
        }),
        catchError(e => this.errorHandler.rethrowHttpError(e, 'realtime-tracking.load-tracking-map.trip-details.errors.refresh-latest-ping'))
      );
  }

  refreshTripETA(trip: Trip, singleTrip: boolean, forceRefresh = false, latestTripDetailsRefreshedAt: Moment): Observable<any> {
    if (trip.isEstimatedETAUpToDate() && !forceRefresh) {
      console.log('Trip ETA is up-to-date');
      return of([]);
    }

    if (!trip.latestPingCreatedAt) {
      console.log('Trip does not have current position');
      return of([]);
    }

    if (trip.isCompleted()) {
      console.log('Trip is completed');
      return of([]);
    }

    if (trip.isExpired()) {
      console.log('Trip is expired');
      return of([]);
    }

    this.refreshEta(trip, singleTrip, latestTripDetailsRefreshedAt);
    return of([]);
  }

  refreshTripRemainingDistance(
    trip: Trip,
    singleTrip: boolean,
    detailedMode: boolean,
    latestTripRemainingDistanceRefreshedAt: Moment
  ): Observable<any> {

    if (!trip.latestPingCreatedAt) {
      console.log('Trip does not have current position');
      return of([]);
    }

    if (trip.isCompleted()) {
      console.log('Trip is completed');
      return of([]);
    }

    if (trip.isExpired()) {
      console.log('Trip is expired');
      return of([]);
    }

    if (!detailedMode) {
      console.log('Don\`t fetch trip remaining distance when no detail mode selected ');
      return of([]);
    }

    this.fetchRemainingDistance(trip, singleTrip, latestTripRemainingDistanceRefreshedAt);
    return of([]);
  }

  refreshTripAchievedPath(
    trip: Trip, forceRefresh = false,
    keepPingsWithHighAccuracy: boolean, excludeIsolatedPings: boolean, hidePingsAfterDestinationHit: boolean
  ): Observable<TripDevicePath[]> {

    if (trip.isAchievedPathUpToDate() && !forceRefresh) {
      console.log('Trip Achieved Path is up-to-date');
      return of([]);
    }

    const maxAccuracy = 150;

    return this.tripService.getPingsLight(trip.tripId, keepPingsWithHighAccuracy ? null : maxAccuracy)
      .pipe(
        map(pings => TripPathUtils.buildPathsFromPingsByDevice(trip, pings, {
          excludeIsolatedPings: excludeIsolatedPings,
          hidePingsAfterDestinationHit: hidePingsAfterDestinationHit
        })),
        tap(paths => {
          console.log(paths);
          trip.achievedPaths             = paths;
          trip.handoverAchievedPath       = [];
          trip.achievedPathsCalculatedAt = moment();
        }),
        catchError(e => this.errorHandler.rethrowHttpError(e, 'realtime-tracking.load-tracking-map.trip-details.errors.refresh-achieved-path'))
      );
  }

  refreshTripForecastPath(
    trip: Trip,
    forceRefresh = false,
    distanceUnit = DistanceUnit.KILOMETERS,
    singleTrip = false
  ): Observable<ForecastAndETA> {
    if (trip.isForecastPathUpToDate() && !forceRefresh) {
      console.log('Trip Forecast Path is up-to-date');
      return of({ forecastPath: trip.forecastPath });
    }

    if (trip.isCompleted()) {
      console.log('Trip is completed');
      return of({ forecastPath: [] });
    }

    if (trip.isExpired()) {
      console.log('Trip is expired');
      return of({ forecastPath: [] });
    }

    let origin: LatLngLiteral;

    if (trip.latestPing) {
      origin = {
        lat: trip.latestPing.latitude,
        lng: trip.latestPing.longitude
      };
    } else if (trip.originPosition) {
      origin = { ...trip.originPosition };
    } else {
      console.log('Trip does not have either current position or origin');
      return of({ forecastPath: [] });
    }

    const departureTime = moment();

    return new Observable<ForecastAndETA>(subscriber => {
      this.mapsAPILoader.load().then(() => {
        const pathCriteria = new PathSearchCriteria(
          origin.lat,
          origin.lng,
          trip.destPosition.lat,
          trip.destPosition.lng,
          distanceUnit
        );
        const forecastPathObservable = singleTrip ?
                                       this.routePathService.searchForCustomer(trip.customerId, pathCriteria) :
                                       this.routePathService.search(pathCriteria);
        forecastPathObservable.subscribe(path => {
          if (path) {
            const forecastPath = path.getForecastPathLatLngLiteral();
            trip.forecastPath             = forecastPath;
            trip.forecastPathCalculatedAt = departureTime;

            subscriber.next({ forecastPath });
            subscriber.complete();
          } else {
            subscriber.error(this.translate.instant('realtime-tracking.load-tracking-map.trip-details.errors.refresh-eta-and-forecast'));
            subscriber.complete();
          }
        });
      }, error => {
        console.error(error);

        subscriber.error(this.translate.instant('realtime-tracking.load-tracking-map.trip-details.errors.load-map'));
        subscriber.complete();
      });
    });
  }

  refreshTripHandovers(trip: Trip, carrier: Carrier): Observable<LoadHandover[]> {
    let loadHandoverObservable = this.loadHandoverService.getLoadHandoversByLoadUuidForCarrier(carrier.id, trip.loadUuid);

    if (this.authService.canReadCarrierLoadHandovers()) {
      loadHandoverObservable = this.loadHandoverService.getLoadHandoversByLoadUuidForSelfCarrier(trip.loadUuid);
    }

    return loadHandoverObservable
      .pipe(
        tap(loadHandovers => trip.loadHandovers = loadHandovers),
        catchError(e => this.errorHandler.rethrowHttpError(e, 'realtime-tracking.load-tracking-map.trip-details.errors.refresh-load-handovers')),
        concatMap(loadHandovers => iif(() => loadHandovers.length > 0,
          forkJoin(loadHandovers.map(l => this.getLoadHandoverAddresses(l)))
            .pipe(mapTo(loadHandovers)),
          of([])
        )),
      );
  }

  refreshTripEvents(trip: Trip): Observable<TripEvent[]> {
    return this.tripService.getTripEvents(trip.tripId)
      .pipe(
        tap(events => trip.events = events),
        catchError(e => this.errorHandler.rethrowHttpError(e, 'realtime-tracking.load-tracking-map.trip-details.errors.refresh-events')),
      );
  }

  refreshTripEtas(trip: Trip): Observable<EtaReport[]> {
    return this.tripService.getTripEtas(trip.deliveryNumbers)
      .pipe(
        tap(etas => trip.etas = etas),
        catchError(e => this.errorHandler.rethrowHttpError(e, 'realtime-tracking.load-tracking-map.trip-details.errors.refresh-etas')),
      );
  }

  private refreshEta(trip: Trip, singleTrip: boolean, latestTripDetailsRefreshedAt: Moment): void {
    if (trip != null) {
      if (null !== trip.latestPingCreatedAt) {
        this.tripService.fetchRefreshEta(trip, latestTripDetailsRefreshedAt, singleTrip)
          .subscribe((eta: RefreshEtas) => {
            if (trip) {
              if (eta) {
                if (eta.driverEta) {
                  trip.driverEta = eta.driverEta.eta;
                  trip.driverEtaUpdatedAt = eta.driverEta.etaUpdatedAt;
                } else if (eta.automaticEta) {
                  if (eta.automaticEta.status === RefreshDataStatus.ERROR) {
                    trip.automaticEta = null;
                    trip.automaticEtaUpdatedAt = null;
                  } else {
                    trip.automaticEta = eta.automaticEta.eta;
                    trip.automaticEtaUpdatedAt = eta.automaticEta.etaUpdatedAt;
                  }
                }
              } else {
                trip.driverEta = null;
                trip.driverEtaUpdatedAt = null;

                trip.automaticEta = null;
                trip.automaticEtaUpdatedAt = null;
              }
            }
          });
      }
    }
  }

  private fetchRemainingDistance(
    trip: Trip, singleTrip: boolean,
    latestTripDistanceRefreshedAt: Moment,
    distanceUnit = DistanceUnit.KILOMETERS) {

    this.tripService.fetchRefreshRemainingDistance(trip, latestTripDistanceRefreshedAt, singleTrip)
      .subscribe((tripRefreshDistance: TripRefreshDistance) => {
        if (tripRefreshDistance.status === RefreshDataStatus.ERROR) {
          trip.distanceToArrival = null;
          this.imperialDistance.set(trip.tripId, null);
          this.metricsDistance.set(trip.tripId, null);
        } else if (tripRefreshDistance.status === RefreshDataStatus.TO_BE_UPDATED) {
          let distance;
          this.imperialDistance.set(trip.tripId, `${Math.round(tripRefreshDistance.imperialDistance / 1000)} miles`);
          this.metricsDistance.set(trip.tripId, `${Math.round(tripRefreshDistance.metricDistance / 1000)} km`);
          if (distanceUnit === DistanceUnit.MILES) {
            distance = this.imperialDistance.get(trip.tripId);
          } else {
            distance = this.metricsDistance.get(trip.tripId);
          }
          trip.distanceToArrival = distance;
        }
      });
  }

  private getLoadHandoverAddresses(loadHandover: LoadHandover): Observable<[string, string]> {
    let releasedAddressObservable = of(loadHandover.releasedAddress);
    const takenAddressObservable    = of(loadHandover.takenAddress);

    if (!loadHandover.releasedAddress && loadHandover.releasedLatitude && loadHandover.releasedLongitude) {
      const latng: Latng = { latitude: loadHandover.releasedLatitude, longitude: loadHandover.releasedLongitude };
      releasedAddressObservable = this.geocodingService.reverseGeocode(latng)
        .pipe(
          delay(7000),
          map(address => address.formatted),
          tap(address => loadHandover.releasedAddress = address),
          tap(address => console.log('released address done', moment().valueOf(), address)),
          catchError(e => this.errorHandler.rethrowHttpError(e, 'realtime-tracking.load-tracking-map.trip-details.errors.get-load-handover-addresses')),
        );
    }

    if (!loadHandover.takenAddress && loadHandover.takenLatitude && loadHandover.takenLongitude) {
      const latng: Latng = { latitude: loadHandover.takenLatitude, longitude: loadHandover.takenLongitude };
      releasedAddressObservable = this.geocodingService.reverseGeocode(latng)
        .pipe(
          map(address => address.formatted),
          tap(address => loadHandover.takenAddress = address),
          tap(address => console.log('taken address done', moment().valueOf(), address)),
          catchError(e => this.errorHandler.rethrowHttpError(e, 'realtime-tracking.load-tracking-map.trip-details.errors.get-load-handover-addresses')),
        );
    }

    return forkJoin([releasedAddressObservable, takenAddressObservable]);
  }
}
